import {
    AlertListTypeEnum,
    AlertOriginTypesEnum,
    AlertSubTypes,
    AlertTypes,
    DataScopesEnum,
    LookupDataEnums,
    MyAlertListSearchStatusOptions,
    MyAlertListSearchStatusOptionsMapping,
    NotePurposeTypesEnum,
    NoteTypes,
    UserRightsEnum,
} from '../../../utilities/Constants';
import { IAlertSearchModel, INoteCategoryTypeModel } from '../../../interfaces/INote';
import { ILookupModel, IMultiSelectOption } from '../../../interfaces/ILookup';
import { IOrganizationRoleModel, IUserModel } from '../../../interfaces/IUser';
import React, { ReactNode, createContext, useContext } from 'react';

import Authorization from '../../../stores/Authorization';
import Sort from '../../../stores/Sort';
import { UseQueryResult } from 'react-query';
import { useAuthContext } from '../../../contexts/AuthContext';
import { useGetLookupData } from '../../../shared/react-query-hooks/useGetLookupData';
import { useNoteCategoryTypes } from '../../../shared/react-query-hooks/useNoteCategoryTypes';
import { useZoneContext } from '../../../contexts/useZoneContext';
import { useZoneUsers } from '../../../shared/react-query-hooks/useZoneUsers';

const lookupToOptionMapper = (lookupItem: ILookupModel) =>
    ({
        id: lookupItem.id,
        guid: lookupItem.guid,
        value: lookupItem.id.toString(),
        label: lookupItem.displayName ?? lookupItem.name,
    }) as IMultiSelectOption;

export const availableTypeOptions: IMultiSelectOption[] = [
    {
        id: AlertTypes.Act.Value,
        label: AlertTypes.Act.Name,
        value: AlertTypes.Act.Name,
    },
    {
        id: AlertTypes.Watch.Value,
        label: AlertTypes.Watch.Name,
        value: AlertTypes.Watch.Name,
    },
];

export const availableSubTypeOptions: IMultiSelectOption[] = [
    {
        id: AlertSubTypes.Collab.Value,
        label: AlertSubTypes.Collab.Name,
        value: AlertSubTypes.Collab.Name,
    },
    {
        id: AlertSubTypes.Chat.Value,
        label: AlertSubTypes.Chat.Name,
        value: AlertSubTypes.Chat.Name,
    },
    {
        id: AlertSubTypes.Review.Value,
        label: AlertSubTypes.Review.Name,
        value: AlertSubTypes.Review.Name,
    },
];

const getAuthorizedOriginTypes = (
    user: IUserModel,
    alertListType: AlertListTypeEnum,
    zoneGuid: string
) => {
    const allowedOriginTypes = [];

    if (Authorization.userHasRight(UserRightsEnum.ViewCaseNotes, user, zoneGuid)) {
        allowedOriginTypes.push(AlertOriginTypesEnum.CaseNote);
    }

    if (Authorization.userHasRight(UserRightsEnum.ViewZoneNotes, user, zoneGuid)) {
        allowedOriginTypes.push(AlertOriginTypesEnum.ZoneNote);
    }

    if (Authorization.userHasViewStrategyNotesRight(user, zoneGuid)) {
        allowedOriginTypes.push(AlertOriginTypesEnum.StrategyNote);
    }

    if (
        Authorization.userHasRight(UserRightsEnum.CaseTriggerRules, user, zoneGuid) &&
        alertListType !== AlertListTypeEnum.ZoneAlertList
    ) {
        allowedOriginTypes.push(AlertOriginTypesEnum.CaseTrigger);
    }

    if (Authorization.userHasRight(UserRightsEnum.ViewPackageSettlement, user, zoneGuid)) {
        allowedOriginTypes.push(AlertOriginTypesEnum.PackageSettlementNote);
    }

    if (
        Authorization.userHasRight(UserRightsEnum.Reports, user, zoneGuid) &&
        alertListType === AlertListTypeEnum.MyAlertList
    ) {
        allowedOriginTypes.push(AlertOriginTypesEnum.Report);
    }

    return allowedOriginTypes;
};

export interface AlertListFiltersContextProps {
    getAlertOriginTypeOptions: (alertListType: AlertListTypeEnum) => IMultiSelectOption[];
    getAlertStatusTypeOptions: (alertListType: AlertListTypeEnum) => IMultiSelectOption[];
    getDisplayTextForSearchModel: (searchModel: IAlertSearchModel) => string;
    getNoteCategoryTypeOptions: (
        selectedOriginTypes: AlertOriginTypesEnum[]
    ) => IMultiSelectOption[];
    getNotePurposeTypeOptions: () => IMultiSelectOption[];
    getNoteSubCategoryTypeOptions: (selectedNoteCategories: number[]) => IMultiSelectOption[];
    getSortedAvailableUserOptions: () => IMultiSelectOption[];

    zoneUsersLookup: UseQueryResult<IUserModel[] | undefined, unknown>;
    alertStatusTypesLookup: UseQueryResult<ILookupModel[] | undefined, unknown>;
    alertOriginTypesLookup: UseQueryResult<ILookupModel[] | undefined, unknown>;
    notePurposeTypesLookup: UseQueryResult<ILookupModel[] | undefined, unknown>;
    noteCategoryTypesLookup: UseQueryResult<ILookupModel[] | undefined, unknown>;
}

const AlertListFiltersContext = createContext<AlertListFiltersContextProps | undefined>(undefined);

interface AlertListFiltersProviderProps {
    alertListType: AlertListTypeEnum;
    children: ReactNode;
}

const AlertListFiltersProvider: React.FC<AlertListFiltersProviderProps> = ({
    alertListType,
    children,
}) => {
    const { user } = useAuthContext();
    const { zoneGuid, zoneApiClient } = useZoneContext();
    const zoneUsersLookup = useZoneUsers(zoneApiClient);
    const alertStatusTypesLookup = useGetLookupData(LookupDataEnums.AlertStatusTypes);
    const alertOriginTypesLookup = useGetLookupData(LookupDataEnums.AlertOriginTypes);
    const notePurposeTypesLookup = useGetLookupData(LookupDataEnums.NotePurposeTypes);
    const noteCategoryTypesLookup = useNoteCategoryTypes();

    // NOTE CATEGORIES
    const getNoteType = (originTypeId: number): number => {
        let noteTypeId;

        switch (originTypeId) {
            case AlertOriginTypesEnum.StrategyNote:
                noteTypeId = NoteTypes.StrategyNote;
                break;
            case AlertOriginTypesEnum.ZoneNote:
                noteTypeId = NoteTypes.ZoneNote;
                break;
            case AlertOriginTypesEnum.PackageSettlementNote:
                noteTypeId = NoteTypes.PackageSettlementNote;
                break;
            default:
                noteTypeId = NoteTypes.CaseNote;
                break;
        }

        return noteTypeId;
    };

    const getValidNoteCategories = (selectedOriginTypes: AlertOriginTypesEnum[]) => {
        const validNoteCategoryFilter = (category: INoteCategoryTypeModel) =>
            selectedOriginTypes
                ?.map((originTypeId) => getNoteType(originTypeId))
                .includes(category.noteType.id);

        const categoryTypes = noteCategoryTypesLookup.data?.filter(validNoteCategoryFilter) ?? [];
        return categoryTypes.sort(Sort.compareValues('label'));
    };

    const getNoteCategoryTypeOptions = (selectedOriginTypes: AlertOriginTypesEnum[]) => {
        const categoryToOptionMap = (cat: INoteCategoryTypeModel) => ({
            label: cat.displayName!,
            value: cat.name!,
            id: cat.id,
        });

        const noteCategoryTypeOptions =
            getValidNoteCategories(selectedOriginTypes).map(categoryToOptionMap);
        return noteCategoryTypeOptions.sort(Sort.compareValues('label'));
    };

    // NOTE SUB-CATEGORIES
    const getNoteSubCategoryTypeOptions = (selectedNoteCategories: number[]) => {
        const noteSubCategoryTypeOptions =
            selectedNoteCategories
                ?.flatMap((catId) => {
                    const match = noteCategoryTypesLookup.data?.find((x) => x.id === catId);
                    return match?.subCategories ?? [];
                })
                .map((item) => ({
                    label: item.displayName!,
                    value: item.name!,
                    id: item.id,
                    parentId: noteCategoryTypesLookup.data?.find((x) =>
                        x.subCategories?.some((sub) => sub.id === item.id)
                    )?.id,
                })) ?? [];

        return noteSubCategoryTypeOptions.sort(Sort.compareValues('label'));
    };

    // USERS
    const getSortedAvailableUserOptions = () => {
        const loggedInUserDataScopeId = Authorization.getUserDefaultOrganizationDataScope(user);

        const defaultOrganization = user.organizationRoles?.find(
            (organizationRole: IOrganizationRoleModel) => organizationRole.default === true
        );
        let availableUserOptions: IMultiSelectOption[] = [];

        const getUserDisplayLabel = (user: IUserModel) => {
            const suffix = Authorization.isLocalScope(user, zoneGuid) ? ' (Local)' : '';
            return `${user.profile!.firstName} ${user.profile!.lastName}${suffix}`;
        };

        if (defaultOrganization) {
            const isLocalBasicWithHomeOrg =
                loggedInUserDataScopeId === DataScopesEnum.LocalBasic.Value &&
                user.homeOrganization;

            if (zoneUsersLookup.data) {
                if (isLocalBasicWithHomeOrg) {
                    const nationalUsers = zoneUsersLookup.data
                        ?.filter(
                            (user) =>
                                Authorization.getUserDefaultOrganizationDataScope(user) ===
                                DataScopesEnum.National.Value
                        )
                        .map((userModel) => ({
                            label: getUserDisplayLabel(userModel),
                            value: userModel.guid!,
                            id: 0,
                            guid: userModel.guid,
                        }));

                    const homeOrgUsers = zoneUsersLookup.data
                        .filter(
                            (x) =>
                                x.homeOrganization &&
                                user.homeOrganization &&
                                x.homeOrganization.guid === user.homeOrganization.guid
                        )
                        .map((userModel: IUserModel) => ({
                            label: getUserDisplayLabel(userModel),
                            value: userModel.guid!,
                            id: 0,
                            guid: userModel.guid,
                        }));

                    availableUserOptions = (nationalUsers ?? []).concat(homeOrgUsers ?? []);
                } else {
                    for (let i = 0; i < zoneUsersLookup.data.length; i++) {
                        const currentUser = zoneUsersLookup.data[i];
                        availableUserOptions.push({
                            label: getUserDisplayLabel(currentUser),
                            value: currentUser.guid!,
                            guid: currentUser.guid!,
                            id: 0,
                        });
                    }
                }
            }
        }
        const availableUserOptionsSorted = availableUserOptions.sort(Sort.compareValues('label'));
        return availableUserOptionsSorted;
    };

    // STATUS TYPES
    const getAlertStatusTypeOptions = () => {
        if (alertListType === AlertListTypeEnum.MyAlertList) {
            return MyAlertListSearchStatusOptions;
        } else if (alertListType === AlertListTypeEnum.ZoneAlertList) {
            return alertStatusTypesLookup.data?.map(lookupToOptionMapper) ?? [];
        }

        return [];
    };

    // ORIGIN TYPES
    const getAlertOriginTypeOptions = () => {
        const permissibleAots = getAuthorizedOriginTypes(user, alertListType, zoneGuid);
        return (
            alertOriginTypesLookup.data
                ?.filter((aot) => permissibleAots.includes(aot.id))
                .map(lookupToOptionMapper) ?? []
        );
    };

    // PURPOSE
    const getNotePurposeTypeOptions = () =>
        notePurposeTypesLookup.data
            ?.filter((x) => x.id !== NotePurposeTypesEnum.Info)
            ?.map(lookupToOptionMapper) ?? [];

    const getDisplayTextForSearchModel = (currentSearchModel: IAlertSearchModel) => {
        let filtersAsString = '';

        if (currentSearchModel.originTypes && currentSearchModel.originTypes.length > 0) {
            filtersAsString += 'TYPES:' + '\n';
            for (let i = 0; i < currentSearchModel.originTypes.length; i++) {
                const match = getAlertOriginTypeOptions().filter(
                    (x) => x.id == currentSearchModel.originTypes![i]
                );
                if (match.length > 0) filtersAsString += match[0].label + '\n';
            }
            filtersAsString += '\n';
        }

        if (currentSearchModel.statuses && currentSearchModel.statuses.length > 0)
            filtersAsString += 'STATUSES:' + '\n';
        {
            if (alertListType === AlertListTypeEnum.MyAlertList) {
                for (let i = 0; i < currentSearchModel.statuses!.length; i++) {
                    const element = currentSearchModel.statuses![i];
                    const id = MyAlertListSearchStatusOptionsMapping.find(
                        (x) => x.status == element.status && x.read == element.read
                    )?.id;
                    const match = MyAlertListSearchStatusOptions.find((x) => x.id === id);

                    if (match) filtersAsString += match.label + '\n';
                }
                filtersAsString += '\n';
            } else if (alertListType === AlertListTypeEnum.ZoneAlertList) {
                for (let i = 0; i < currentSearchModel.statuses!.length; i++) {
                    const match = getAlertStatusTypeOptions().filter(
                        (x) => x.id == currentSearchModel.statuses![i].status
                    );
                    if (match.length > 0) filtersAsString += match[0].label + '\n';
                }
                filtersAsString += '\n';
            }
        }

        if (currentSearchModel.alertTypeIds && currentSearchModel.alertTypeIds.length > 0) {
            filtersAsString += 'ALERT TYPES:' + '\n';
            for (let i = 0; i < currentSearchModel.alertTypeIds.length; i++) {
                const match = availableTypeOptions.filter(
                    (x) => x.id == currentSearchModel.alertTypeIds![i]
                );
                if (match.length > 0) filtersAsString += match[0].label + '\n';
            }
            filtersAsString += '\n';
        }

        if (currentSearchModel.alertSubTypeIds && currentSearchModel.alertSubTypeIds.length > 0) {
            filtersAsString += 'ALERT SUB-TYPES:' + '\n';
            for (let i = 0; i < currentSearchModel.alertSubTypeIds.length; i++) {
                const match = availableSubTypeOptions.filter(
                    (x) => x.id == currentSearchModel.alertSubTypeIds![i]
                );
                if (match.length > 0) filtersAsString += match[0].label + '\n';
            }
            filtersAsString += '\n';
        }

        if (currentSearchModel.alertUserGuids && currentSearchModel.alertUserGuids.length > 0) {
            filtersAsString += 'USERS:' + '\n';
            for (let i = 0; i < currentSearchModel.alertUserGuids.length; i++) {
                const match = getSortedAvailableUserOptions().filter(
                    (x) => x.guid == currentSearchModel.alertUserGuids![i]
                );
                if (match.length > 0) filtersAsString += match[0].label + '\n';
            }
            filtersAsString += '\n';
        }

        if (currentSearchModel.noteCategories && currentSearchModel.noteCategories.length > 0) {
            filtersAsString += 'NOTE CATEGORIES:' + '\n';
            for (let i = 0; i < currentSearchModel.noteCategories.length; i++) {
                const match = noteCategoryTypesLookup.data?.find(
                    (x) => x.id === currentSearchModel.noteCategories![i]
                );
                if (match) filtersAsString += match.displayName + '\n';
            }
            filtersAsString += '\n';
        }

        if (
            currentSearchModel.noteSubCategories &&
            currentSearchModel.noteSubCategories.length > 0
        ) {
            filtersAsString += 'NOTE SUB-CATEGORIES:' + '\n';
            for (let i = 0; i < currentSearchModel.noteSubCategories.length; i++) {
                for (let index = 0; index < (noteCategoryTypesLookup.data?.length ?? 0); index++) {
                    const element = noteCategoryTypesLookup.data?.[index];
                    if (element?.subCategories) {
                        const match = element.subCategories.find(
                            (x) => x.id === currentSearchModel.noteSubCategories![i]
                        );
                        if (match) filtersAsString += match.displayName + '\n';
                    }
                }
            }
            filtersAsString += '\n';
        }

        if (currentSearchModel.purpose && currentSearchModel.purpose.length > 0) {
            filtersAsString += 'PURPOSES:' + '\n';
            for (let i = 0; i < currentSearchModel.purpose.length; i++) {
                const match = getNotePurposeTypeOptions().filter(
                    (x) => x.id == currentSearchModel.purpose![i]
                );
                if (match.length > 0) filtersAsString += match[0].label + '\n';
            }
            filtersAsString += '\n';
        }

        return filtersAsString;
    };

    return (
        <AlertListFiltersContext.Provider
            value={{
                getAlertOriginTypeOptions,
                getAlertStatusTypeOptions,
                getDisplayTextForSearchModel,
                getNoteCategoryTypeOptions,
                getNotePurposeTypeOptions,
                getNoteSubCategoryTypeOptions,
                getSortedAvailableUserOptions,

                alertOriginTypesLookup,
                alertStatusTypesLookup,
                notePurposeTypesLookup,
                noteCategoryTypesLookup,
                zoneUsersLookup,
            }}
        >
            {children}
        </AlertListFiltersContext.Provider>
    );
};

const useAlertListFilters = (): AlertListFiltersContextProps => {
    const context = useContext(AlertListFiltersContext);
    if (!context) {
        throw new Error('useAlertListFilters must be used within an AlertListFiltersProvider');
    }
    return context;
};

export { AlertListFiltersProvider, useAlertListFilters };
